import { useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { entrar } from '../../auth';
import type { FalhaDeLogin, Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { SolicitarAcesso } from './SolicitarAcesso';
import './login.css';
import { propsDeLoginQueNaoSalva, propsDeSenhaQueNaoSalva } from '../../design/semSalvarSenha';

/**
 * TELA 04 — LOGIN. "Uma direção, feita com cuidado."
 *
 * Split com o painel grafite dando palco à logo oficial e o painel claro com um único objetivo:
 * entrar. A malha fina, a curva de luz e a linha vermelha que atravessa o painel são o detalhe
 * proprietário — sem números nem gráficos falsos.
 *
 * Os estados vêm do design, um por um: vazio, foco, preenchido, Caps Lock, credenciais inválidas,
 * carregando, sem conexão, serviço indisponível e erro inesperado. Os alertas aparecem DENTRO do
 * formulário, acima dos campos, com o espaço já reservado (a tela não pula quando o erro chega).
 */

type Tela = 'login' | 'esqueci' | 'solicitar';

const TEXTO_DA_FALHA: Record<FalhaDeLogin['tipo'], { titulo: string; detalhe: string }> = {
  credenciais: {
    titulo: 'Não foi possível entrar.',
    detalhe: 'Confira seus dados e tente novamente.',
  },
  limite: {
    titulo: 'Muitas tentativas seguidas.',
    detalhe: 'Aguarde alguns minutos e tente novamente.',
  },
  offline: {
    titulo: 'Não foi possível conectar.',
    detalhe: 'Verifique sua conexão e tente novamente.',
  },
  indisponivel: {
    titulo: 'O InovaPay está temporariamente indisponível.',
    detalhe: 'Tente novamente em instantes.',
  },
  inesperado: { titulo: 'Algo deu errado ao entrar.', detalhe: 'Tente novamente.' },
};

export function Login({ aoEntrar }: { aoEntrar: (sessao: Sessao) => void }) {
  const [tela, setTela] = useState<Tela>('login');
  const [login, setLogin] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<FalhaDeLogin | null>(null);
  const campoLogin = useRef<HTMLInputElement>(null);

  const lerCaps = (e: KeyboardEvent<HTMLInputElement>) =>
    setCapsLock(e.getModifierState('CapsLock'));

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return; // "três cliques não geram três sessões"
    if (!login.trim() || !senha) {
      setFalha({ tipo: 'credenciais' });
      return;
    }
    setEnviando(true);
    setFalha(null);
    const r = await entrar(login.trim(), senha);
    setEnviando(false);
    if (r.ok) {
      setSenha('');
      aoEntrar(r.sessao);
      return;
    }
    // A senha sai do campo em qualquer recusa: ela não fica na memória da tela nem visível.
    if (r.falha.tipo === 'credenciais') setSenha('');
    setFalha(r.falha);
  }

  const alerta = falha ? TEXTO_DA_FALHA[falha.tipo] : null;
  const rotuloBotao = falha?.tipo === 'offline' ? 'Tentar de novo' : 'Entrar';

  return (
    <div className="lg">
      <aside className="lg-hero" aria-hidden="false">
        <div className="lg-hero-fundo" />
        <div className="lg-hero-malha" />
        <svg
          className="lg-hero-curvas"
          viewBox="0 0 1000 1000"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M-80 760 C 220 520, 520 900, 1080 380"
            fill="none"
            stroke="rgba(255,255,255,.06)"
            strokeWidth="90"
          />
          <path
            className="lg-linha"
            d="M-80 700 C 240 470, 540 860, 1080 320"
            fill="none"
            stroke="rgba(224,74,87,.55)"
            strokeWidth="1.6"
          />
          <path
            d="M-80 830 C 260 610, 560 960, 1080 470"
            fill="none"
            stroke="rgba(255,255,255,.10)"
            strokeWidth="1"
          />
          <circle cx="540" cy="705" r="3.5" fill="#E04A57" />
          <circle cx="860" cy="470" r="2.5" fill="rgba(255,255,255,.4)" />
        </svg>
        <div className="lg-hero-conteudo">
          <div className="lg-hero-meio">
            <img className="lg-logo" src="/img/inovapay-logo-hd.png" alt="InovaPay" />
            <div className="lg-hero-rede">REDE INOVA DROGARIAS</div>
            <h2 className="lg-hero-titulo">
              A operação da Rede Inova,
              <br />
              <span>em um só lugar.</span>
            </h2>
            <p className="lg-hero-texto">
              Vendas, premiação, Giro de Prateleira e gestão conectados em uma única experiência.
            </p>
          </div>
          <div className="lg-hero-rodape">
            <div>
              <div className="lg-mono lg-rede">REDE INOVA DROGARIAS</div>
              <div className="lg-cidade">Muriaé · MG</div>
            </div>
            <div style={{ flex: 1 }} />
            <span className="lg-ambiente">
              <span className="lg-ambiente-ponto" />
              {import.meta.env.PROD ? 'PRODUÇÃO' : 'DESENVOLVIMENTO'}
            </span>
          </div>
        </div>
      </aside>

      <main className="lg-painel">
        <div className="lg-lema">
          <span className="lg-lema-traco" />
          UMA REDE MAIS FORTE, TODOS OS DIAS
        </div>

        <div className="lg-centro">
          {tela === 'login' && (
            <form
              className="lg-form"
              autoComplete="off"
              onSubmit={(e) => void enviar(e)}
              noValidate
            >
              <h1 className="lg-titulo">Bem-vindo de volta</h1>
              <p className="lg-sub">Acesse sua conta para continuar.</p>

              {/* Espaço do alerta já reservado: o formulário não pula quando o erro aparece. */}
              <div className="lg-alerta-lugar" aria-live="assertive">
                {alerta && (
                  <div className="lg-alerta" role="alert">
                    <Icone nome="warn" tamanho={17} traco={1.7} />
                    <div>
                      <div className="lg-alerta-titulo">{alerta.titulo}</div>
                      <div className="lg-alerta-detalhe">
                        {alerta.detalhe}
                        {falha?.tipo === 'inesperado' && falha.codigo
                          ? ` Se persistir, informe o código ${falha.codigo} ao suporte.`
                          : ''}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="lg-campos">
                <div>
                  <label className="lg-rotulo" htmlFor="lg-login">
                    Código ou usuário
                  </label>
                  <div
                    className={`lg-campo${falha?.tipo === 'credenciais' ? ' lg-campo-erro' : ''}`}
                  >
                    <Icone nome="user" />
                    <input
                      id="lg-login"
                      ref={campoLogin}
                      value={login}
                      onChange={(e) => setLogin(e.target.value)}
                      placeholder="Digite seu código ou usuário"
                      {...propsDeLoginQueNaoSalva}
                      autoCapitalize="none"
                      spellCheck={false}
                      disabled={enviando}
                      aria-invalid={falha?.tipo === 'credenciais'}
                    />
                  </div>
                </div>
                <div>
                  <label className="lg-rotulo" htmlFor="lg-senha">
                    Senha
                  </label>
                  <div
                    className={`lg-campo${falha?.tipo === 'credenciais' ? ' lg-campo-erro' : ''}`}
                  >
                    <Icone nome="lock" />
                    <input
                      id="lg-senha"
                      {...propsDeSenhaQueNaoSalva(mostrarSenha)}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      onKeyDown={lerCaps}
                      onKeyUp={lerCaps}
                      placeholder="Digite sua senha"
                      disabled={enviando}
                      aria-invalid={falha?.tipo === 'credenciais'}
                      aria-describedby={capsLock ? 'lg-caps' : undefined}
                    />
                    <button
                      type="button"
                      className="lg-olho"
                      onClick={() => setMostrarSenha((v) => !v)}
                      aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      aria-pressed={mostrarSenha}
                    >
                      <Icone nome="eye" />
                    </button>
                  </div>
                  {capsLock && (
                    <div className="lg-caps" id="lg-caps">
                      <Icone nome="caps" tamanho={15} />
                      Caps Lock ativado
                    </div>
                  )}
                </div>
              </div>

              <div className="lg-esqueci">
                <a
                  href="#esqueci"
                  onClick={(e) => {
                    e.preventDefault();
                    setTela('esqueci');
                  }}
                >
                  Esqueceu sua senha?
                </a>
              </div>

              <button className="lg-entrar" type="submit" disabled={enviando}>
                {enviando ? (
                  <span className="lg-entrando">
                    <span className="lg-spin" aria-hidden="true" />
                    Entrando…
                  </span>
                ) : (
                  <>
                    {rotuloBotao}
                    <Icone nome="arrow" />
                  </>
                )}
              </button>

              <div className="lg-ou">
                <span>Ainda não possui acesso?</span>
              </div>
              <button type="button" className="lg-solicitar" onClick={() => setTela('solicitar')}>
                Solicitar acesso
              </button>
            </form>
          )}

          {tela === 'esqueci' && (
            <div className="lg-form">
              <h1 className="lg-titulo">Esqueceu sua senha?</h1>
              <p className="lg-sub">
                A senha é redefinida por um administrador autorizado da Rede Inova. Procure o seu
                administrador para receber uma nova.
              </p>
              <button
                type="button"
                className="lg-solicitar lg-voltar"
                onClick={() => setTela('login')}
              >
                Voltar para o login
              </button>
            </div>
          )}

          {tela === 'solicitar' && <SolicitarAcesso aoVoltar={() => setTela('login')} />}
        </div>

        <footer className="lg-rodape">
          <span>InovaPay · Rede Inova Drogarias</span>
          <span className="lg-mono lg-cnpj">CNPJ 03.566.388/0001-80</span>
        </footer>
      </main>
    </div>
  );
}
